import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  Settings as SettingsIcon,
  ShieldCheck,
  LogOut,
  HelpCircle,
} from 'lucide-react';
import { useAuth, AppRole } from '../../context/AuthContext';
import { useHelp } from '../../help';

const ROLE_LABEL: Record<AppRole, string> = {
  admin:     'Admin',
  inspector: 'Inspector',
  viewer:    'Operator',
};

const ROLE_COLOR: Record<AppRole, string> = {
  admin:     'text-purple-300 bg-purple-900/40 border-purple-700/60',
  inspector: 'text-orange-300 bg-orange-900/40 border-orange-700/60',
  viewer:    'text-cyan-300 bg-cyan-900/40 border-cyan-700/60',
};

/**
 * Account menu — avatar chip + dropdown. Deliberately ACCOUNT-scoped:
 * navigation destinations live in the side rail and the top bar's primary
 * action, so this menu no longer mirrors half the nav (the old version
 * carried inspections/templates/demo links twice over).
 */
export default function UserMenu() {
  const { profile, role, signOut } = useAuth();
  const navigate = useNavigate();
  const { openHelp } = useHelp();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!profile) return null;

  const initials = profile.full_name
    ? profile.full_name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : profile.email.slice(0, 2).toUpperCase();

  const item =
    'w-full flex items-center gap-2.5 px-3.5 py-2 text-[13px] text-slate-300 hover:bg-slate-800/70 hover:text-white transition-colors text-left';

  return (
    <div className="relative shrink-0" ref={menuRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 pl-1 pr-1.5 py-1 rounded-lg hover:bg-slate-800/60 transition-all duration-150 active:scale-[0.98]"
        aria-label="Account menu"
      >
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-[11px] font-bold shrink-0 shadow-[0_0_10px_rgba(6,182,212,0.25)]">
          {initials}
        </div>
        {role && (
          <span className={`hidden sm:inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded border ${ROLE_COLOR[role]}`}>
            {ROLE_LABEL[role]}
          </span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-60 bg-slate-900 border border-slate-700/80 rounded-xl shadow-2xl shadow-black/50 z-50 py-1 overflow-hidden">
          {/* Identity */}
          <div className="px-3.5 py-3 border-b border-slate-800 flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
              {initials}
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-white truncate">{profile.full_name || '—'}</div>
              <div className="text-[11px] text-slate-400 truncate">{profile.email}</div>
              {profile.organisation && (
                <div className="text-[11px] text-slate-500 truncate">{profile.organisation}</div>
              )}
            </div>
          </div>

          <div className="py-1">
            {(role === 'inspector' || role === 'admin') && (
              <button onClick={() => { setOpen(false); navigate('/vetting/settings'); }} className={item}>
                <SettingsIcon className="w-4 h-4 text-slate-400" />
                <span>Settings</span>
              </button>
            )}
            {role === 'admin' && (
              <button onClick={() => { setOpen(false); navigate('/admin'); }} className={item}>
                <ShieldCheck className="w-4 h-4 text-purple-300" />
                <span>Admin panel</span>
              </button>
            )}
            <button onClick={() => { setOpen(false); openHelp(); }} className={item}>
              <HelpCircle className="w-4 h-4 text-cyan-400" />
              <span>Help</span>
              <span className="ml-auto text-[10px] text-slate-500 font-mono">?</span>
            </button>
          </div>

          <div className="border-t border-slate-800 py-1">
            <button
              onClick={async () => { setOpen(false); await signOut(); navigate('/login'); }}
              className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[13px] text-red-400 hover:bg-red-950/40 transition-colors text-left"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
